import React, {FC} from 'react'
import classnames from 'classnames'
import {useTranslation} from 'react-i18next'
import styled from 'styled-components'

import {WarningIcon} from '../SvgIcon'
import {UseFormRegister} from 'react-hook-form'
import {colorCodes} from 'definitions/constant'

const CheckboxContainer = styled.div`
  display: flex;
  justify-content: center;
`

const Icon = styled.svg`
  fill: none;
  stroke: white;
  stroke-width: 2px;
`
// Hide checkbox visually but remain accessible to screen readers.
// Source: https://polished.js.org/docs/#hidevisually
const HiddenCheckbox = styled.input.attrs({type: 'checkbox'})`
  border: 0;
  clip: rect(0 0 0 0);
  clippath: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`

const StyledCheckbox = styled.div<{
  checked: boolean
  disabled: boolean | undefined
  defaultColor?: string
}>`
  display: inline-block;
  width: 16px;
  height: 16px;
  background: ${props =>
    props.disabled
      ? colorCodes.gray1100
      : props.checked
      ? colorCodes.white
      : props.defaultColor || colorCodes.dopeBlack};
  transition: all 150ms;
  border: ${props =>
    props.disabled
      ? `1px solid ${colorCodes.gray1100}`
      : `1px solid ${colorCodes.white}`};
  border-radius: 3px;
  ${HiddenCheckbox}:focus + & {
    // box-shadow: 0 0 0 3px pink;
  }

  ${Icon} {
    visibility: ${props => (props.checked ? 'visible' : 'hidden')};
    stroke: ${props =>
      props.checked ? colorCodes.dopeBlack : colorCodes.white};
  }
  &:hover {
    background: ${props => (props.disabled ? '' : colorCodes.gray1100)};
    border: ${props =>
      !props.disabled
        ? `1px solid ${colorCodes.white}`
        : `1px solid ${colorCodes.gray1100}`};
  }
`

interface LabelProps {
  htmlFor: string
  id?: string
  className?: string
  children?: React.ReactNode
  error?: string
}

interface InputProps {
  className?: string
  children?: React.ReactNode
  bgColor?: string
  type?: 'email' | 'password' | 'text' | 'tel'
  id: string
  name: string
  placeholder?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  onBlur?: React.ChangeEventHandler<HTMLInputElement>
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>
  error?: string
  list?: string
  autoComlete?: 'on' | 'off'
  disabled?: boolean
}

interface TextAreaProps {
  className?: string
  children?: React.ReactNode
  bgColor?: string
  rows?: number
  id: string
  name: string
  placeholder?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>
  onBlur?: React.ChangeEventHandler<HTMLTextAreaElement>
  error?: string
  value?: string
  disabled?: boolean
}

export const FormInput: FC<InputProps> = ({
  bgColor = 'dope-black',
  type = 'text',
  name,
  className,
  id,
  register,
  placeholder,
  error,
  onBlur,
  onChange,
  autoComlete = 'off',
  disabled = false,
  ...rest
}) => {
  return (
    <input
      {...register(name, {
        onChange,
        onBlur,
      })}
      {...rest}
      placeholder={placeholder}
      id={id}
      name={name}
      type={type}
      className={classnames(
        `appearance-none border border-gray-600 w-full py-2 px-2 leading-tight focus:outline-none bg-${bgColor} placeholder-gray-700 ${className}`,
        {
          'border-red-500': error,
          'text-red-500': error,
          'placeholder-red-500': error,
          'focus:border-red-500': error,
          'focus:shadow-outline-red': error,
          'focus:shadow-outline': !error,
        },
      )}
      autoComplete={autoComlete}
      disabled={disabled}
    />
  )
}

export const FormTextArea: FC<TextAreaProps> = ({
  bgColor = 'dope-black',
  rows = 5,
  name,
  className,
  id,
  register,
  placeholder,
  error,
  onBlur,
  onChange,
  disabled = false,
  ...rest
}) => {
  return (
    <textarea
      {...register(name, {
        onChange,
        onBlur,
      })}
      {...rest}
      placeholder={placeholder}
      id={id}
      name={name}
      rows={rows}
      disabled={disabled}
      className={classnames(
        `${className} appearance-none form-textarea border border-gray-600 w-full p-2 leading-tight focus:outline-none bg-${bgColor} placeholder-gray-700`,
        {
          'border-red-500': error,
          'text-red-500': error,
          'placeholder-red-500': error,
          'focus:border-red-500': error,
          'focus:shadow-outline-red': error,
          'focus:shadow-outline': !error,
        },
      )}
    />
  )
}

export const FormLabel: FC<LabelProps> = ({
  htmlFor,
  className = 'text-gray-600',
  children,
  error,
  id,
}) => {
  return (
    <section className="relative">
      <div
        className={classnames(
          'absolute inset-y-0 right-0 flex items-center cursor-pointer',
          {
            hidden: !error,
          },
        )}
      >
        <WarningIcon />
      </div>
      <label
        className={classnames(
          `${className} block text-left text-xs leading-snug font-light mb-2`,
        )}
        id={id}
        htmlFor={htmlFor}
      >
        {children}
      </label>
    </section>
  )
}

interface FormCheckboxProps {
  className?: string
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  checked: boolean
  id: string
  disabled?: boolean
  defaultColor?: string
  partialChecked?: boolean
  role?: string
  ariaLabel?: string
}
export const FormCheckbox: FC<FormCheckboxProps> = ({
  className,
  checked,
  defaultColor,
  partialChecked = false,
  ...props
}) => {
  const {disabled, ariaLabel} = props
  const {t} = useTranslation(['common'])
  return (
    <CheckboxContainer data-cy="checkbox-container" className={className}>
      <HiddenCheckbox checked={checked} aria-label={ariaLabel} {...props} />
      <StyledCheckbox
        checked={checked}
        disabled={disabled}
        defaultColor={defaultColor}
        aria-label={t(checked ? 'CHECKED' : 'UNCHECKED')}
      >
        <Icon viewBox="0 0 24 24">
          {!partialChecked ? (
            <polyline points="20 6 9 17 4 12" />
          ) : (
            <rect x="5" y="12" width="14" height="0.5" />
          )}
        </Icon>
      </StyledCheckbox>
    </CheckboxContainer>
  )
}

export const FormError: FC<{id?: string; error?: string; classes?: string}> = ({
  error,
  id = 'form-error',
  classes,
}) => {
  return (
    <p
      data-cy={id}
      role="alert"
      className={classnames(`mt-1 text-sm text-red-500 ${classes}`, {
        hidden: !error,
      })}
    >
      {error}
    </p>
  )
}
